import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
} from '@mui/material';
import TextInputBox from '../../components/TextInputBox';
import RadioGroupBox from '../../components/RadioGroupBox';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import ButtonBox from '../../components/ButtonBox';
import { useNavigate } from 'react-router-dom';
import { ROUTE_THING, ROUTE_THING_INFOMAIN, THING_ID } from '../../constants';
import IconButtonBox from '../../components/IconButtonBox';
import '../../style/Thing.css';
import ThingBucket from '../../components/ThingBucket';
import SelectBox from '../../components/SelectBox';
import ThingConfig from '../../components/ThingConfig';
import ProjectApi from '../../api/ProjectApi';
import { Option } from "../../types/OptionType";
import useThingStore from '../../stores/ThingStore';
import { Thing } from '../../types/ThingType';
import ThingApi from '../../api/ThingApi';
import { getLocalStorage, getRandomId, useError } from '../../utils/functions';

interface ThingInfo {
  deviceName: string;
  serialNumber: string;
  modelName: string;
  description: string;
  visibility: 'private' | 'public';
  thingType: 'Device' | 'EoN';
  coordinatesLng: number;
  coordinatesLat: number;
  projectToConnect: string;
}

const ThingCreateEdit: React.FC = () => {
  const { setError } = useError();
  const navigate = useNavigate();
  const [options, setOptions] = useState<Option[]>([]);

  const { labelBuckets, labelConfigs, thingObj, setThingObj, isEdit } = useThingStore();

  const [thingInfo, setThingInfo] = useState<ThingInfo>({
    deviceName: '',
    serialNumber: '',
    modelName: '',
    description: '',
    visibility: 'private',
    thingType: 'Device',
    coordinatesLng: 0,
    coordinatesLat: 0,
    projectToConnect: '',
  });

  const [items, setItems] = useState([{ id: getRandomId(), labelKey: "", labelValue: "" }]);

  const goTo = () => {
    thingObj.id ? navigate(ROUTE_THING_INFOMAIN) : navigate(ROUTE_THING);
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setThingInfo({
      ...thingInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddItem = () => {
    setItems([...items, { id: getRandomId(), labelKey: "", labelValue: "" }]);
  };

  const handleDeleteItem = (id: any) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const handleLabelChange = (id: any, event: any) => {
    const { name, value } = event.target;
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, [name]: value } : item
      )
    );
  };

  const handleCreateEdit = async () => {

    const labels = [];
    for (const item of items) {
      if (item.labelKey && item.labelValue) {
        labels.push({ key: item.labelKey, value: item.labelValue });
      }
    }

    const newThing: Thing = {
      modelName: thingInfo.modelName,
      serialNumber: thingInfo.serialNumber,
      name: thingInfo.deviceName,
      description: thingInfo.description,
      coordinates: [Number(thingInfo.coordinatesLng), Number(thingInfo.coordinatesLat)],
      isEon: thingInfo.thingType === 'EoN',
      visibility: thingInfo.visibility,
      labels: labels,
      projectId: thingInfo.projectToConnect
    };

    let isError = false;
    if (thingObj.id) {
      const res = await ThingApi.updateThing(thingObj.id, newThing);
      if (res.code) {
        setError(res.message);
        isError = true;
      }

      const orgLabels = thingObj.labels ? thingObj.labels : [];
      for (let label of orgLabels) {
        await ThingApi.deleteLabel(thingObj.id, label.key);
      }

      for (let label of labels) {
        await ThingApi.putLabel(thingObj.id, label.key, label.value);
      }

    } else {
      const res = await ThingApi.createThing(newThing);

      if (res.code) {
        setError(res.message);
        isError = true;
      } else {
        const id = res?.data?.id;

        for (let bucket of labelBuckets) {
          const res = await ThingApi.createDataBucket(id, bucket);
          if (res.code) {
            setError(res.message);
            isError = true;
            await ThingApi.deleteThing(id);
            break;
          }
        }


        for (let config of labelConfigs) {
          const name = config.name;
          const configs = {
            configs: config.dataKeys.reduce((obj: { [key: string]: any }, { key, value, valueType }) => {
              if (valueType === "JSON") {
                obj[key] = JSON.parse(value);
              } else if (valueType === "Array") {
                obj[key] = JSON.parse(value);
              } else if (valueType === "string") {
                obj[key] = value;
              } else if (valueType === "boolean") {
                obj[key] = (value === "true");
              } else if (valueType === "number") {
                obj[key] = Number(value);
              }
              return obj;
            }, {})
          };

          const res = await ThingApi.createUpdateConfig(id, name, configs);
          if (res.code) {
            setError(res.message);
            isError = true;
            await ThingApi.deleteThing(id);
            break;
          }
        }

      }
    }

    if (!isError) {
      goTo();
    }

  }

  const getThing = async () => {
    let obj = {} as Thing;
    if (thingObj.id) {
      obj = thingObj;
    } else if (getLocalStorage(THING_ID) !== '') {
      const thingId = getLocalStorage(THING_ID);
      const res = await ThingApi.getThingById(thingId);

      if (res.code) {
        setError(res.message);
      } else {
        setThingObj(res.data);
        obj = res.data;
      }
    }

    if (obj.id) {
      setThingInfo({
        deviceName: obj.name ? obj.name : '',
        serialNumber: obj.serialNumber ? obj.serialNumber : '',
        modelName: obj.modelName ? obj.modelName : '',
        description: obj.description ? obj.description : '',
        visibility: obj.visibility === 'public' ? 'public' : 'private',
        thingType: obj.isEon ? 'EoN' : 'Device',
        coordinatesLng: obj.coordinates && obj.coordinates[0] ? obj.coordinates[0] : 0,
        coordinatesLat: obj.coordinates && obj.coordinates[1] ? obj.coordinates[1] : 0,
        projectToConnect: obj.projectId ? obj.projectId : '',
      });

      const labels = obj.labels ? obj.labels : [];
      const newLabels = [];

      for (let label of labels) {
        newLabels.push({ id: getRandomId(), labelKey: label.key, labelValue: label.value });
      }

      setItems(newLabels);
    }
  };

  const getAllProject = async () => {
    setOptions([]);
    const res = await ProjectApi.getAllProject();

    if (res) {
      if (res.code) {
        setError(res.message);
      } else {
        const newOptions = Object.values(res).map((obj: any) => ({
          value: obj.id,
          label: obj.name
        }));

        setOptions(newOptions);
      }
    }
  };

  useEffect(() => {
    getAllProject();
    getThing();
  }, []);

  return (
    <Box>
      <Typography className='thing_typography_title'>{thingObj.id ? "Edit thing" : "Create a thing"}</Typography>
      <Paper sx={{ padding: 4, position: 'relative', width: '80%' }}>
        <Box className='line_box' />
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>Thing Information</Typography>
        <TextInputBox
          label="Device Name"
          name="deviceName"
          required={true}
          value={thingInfo.deviceName}
          onChange={handleInputChange}
          placeholder="e.g. Temperature Sensor"
          className='w400'
        />

        <TextInputBox
          label="Model Name"
          name="modelName"
          required={true}
          value={thingInfo.modelName}
          onChange={handleInputChange}
          placeholder="e.g. ORIO-G30218"
          className='w400'
        />

        <TextInputBox
          label="Serial Number"
          name="serialNumber"
          required={true}
          disabled={thingObj.id ? true : false}
          value={thingInfo.serialNumber}
          onChange={handleInputChange}
          placeholder="e.g. 1234abc"
          className='w400'
        />

        <TextInputBox
          label="Description"
          name="description"
          value={thingInfo.description}
          onChange={handleInputChange}
          placeholder="Describe your device"
          className='w400'
        />

        <SelectBox
          label="Project to Connect"
          name="projectToConnect"
          value={thingInfo.projectToConnect}
          disabled={isEdit}
          onChange={(e: any) => handleInputChange(e)}
          className='w400'
          options={options}
          required={true}
        />

        <RadioGroupBox
          label="Visibility"
          labelSize="h6"
          name="visibility"
          required={true}
          value={thingInfo.visibility}
          onChange={(e) =>
            setThingInfo({ ...thingInfo, visibility: e.target.value as 'private' | 'public' })
          }
          options={[
            { value: 'private', label: 'Private' },
            { value: 'public', label: 'Public' },
          ]}
          sx={{ mb: 2 }}
        />

        <RadioGroupBox
          label="Thing Type"
          labelSize="h6"
          name="thingType"
          required={true}
          value={thingInfo.thingType}
          onChange={(e) =>
            setThingInfo({ ...thingInfo, thingType: e.target.value as 'Device' | 'EoN' })
          }
          options={[
            { value: 'Device', label: 'Device' },
            { value: 'EoN', label: 'EoN' },
          ]}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h6">Coordinates</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextInputBox
              label=""
              name="coordinatesLng"
              value={thingInfo.coordinatesLng}
              onChange={handleInputChange}
              placeholder="Longitude"
              className='w200'
            />
            <TextInputBox
              label=""
              name="coordinatesLat"
              value={thingInfo.coordinatesLat}
              onChange={handleInputChange}
              placeholder="Longitude"
              className='w200'
            />
          </Box>

          <Typography variant="h6">Label</Typography>
          {items.map((item) => (
            <Box key={item.id} sx={{ display: "flex", gap: 2, alignItems: "center", marginBottom: 2 }}>
              <TextInputBox
                name="labelKey"
                value={item.labelKey}
                onChange={(e) => handleLabelChange(item.id, e)}
                placeholder="Key"
                className="w200"
              />
              <TextInputBox
                name="labelValue"
                value={item.labelValue}
                onChange={(e) => handleLabelChange(item.id, e)}
                placeholder="Value"
                className="w200"
              />
              <IconButtonBox icon={<DeleteOutlineOutlinedIcon />} onClick={() => handleDeleteItem(item.id)} />
            </Box>
          ))}
          <Box>
            <ButtonBox
              variant="outlined"
              text='Add'
              onClick={handleAddItem}
              icon={<AddIcon />} />
          </Box>
        </Box>


        <Box sx={{ padding: 2 }}>
          {!thingObj.id && (
            <>
              <ThingBucket title='Data bucket' />
              <ThingConfig title='Configuration' />
            </>
          )
          }
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <ButtonBox
              variant="outlined"
              text="Cancel"
              onClick={goTo} />

            <ButtonBox
              variant="contained"
              text={thingObj.id ? "Save" : "Create"}
              onClick={handleCreateEdit} />
          </Box>
        </Box>
      </Paper>
    </Box >
  );
};

export default ThingCreateEdit;
